/**
 * SurfaceHeader — consistent page header for each RCC surface.
 *
 * Shows: back-to-dashboard link, title, scope sentence, and optional action slot.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Pill } from '@/components/primitives';

export interface SurfaceHeaderProps {
  title: string;
  scope: string;
  surfaceNumber?: number;
  actions?: ReactNode;
  role?: string;
}

export function SurfaceHeader({
  title,
  scope,
  surfaceNumber,
  actions,
  role,
}: SurfaceHeaderProps): JSX.Element {
  return (
    <header
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-8)',
        paddingBottom: 'var(--sp-5)',
        borderBottom: '1px solid var(--surface-border)',
      }}
    >
      <Link
        to="/rcc"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          fontSize: 12,
          color: 'var(--pcb-blue)',
          textDecoration: 'none',
          width: 'fit-content',
        }}
      >
        <ChevronLeft size={14} /> Back to Compliance Center
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)' }}>
        <h1 style={{ margin: 0 }}>{title}</h1>
        {surfaceNumber !== undefined && <Pill variant="muted">Surface #{surfaceNumber}</Pill>}
        {role && <Pill variant="info">{role}</Pill>}
        {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
      </div>
      <p style={{ margin: 0, color: 'var(--ink-600)', fontSize: 13 }}>{scope}</p>
    </header>
  );
}
